import { useEffect, useState, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { API_ERRORS, type MinimumVersion } from "@shared/contracts";
import { ErrorToast } from "@/app/components/ErrorToast";
import { UpdateGate } from "@/app/components/UpdateGate";
import { onGlobalApiError } from "@/app/lib/api-client";
import { checkVersion, type VersionVerdict } from "@/app/lib/version";
import { identifyUser, initObservability } from "@/app/observability";
import { queryClient, setSessionLostHandler } from "@/app/query/query-client";
import { useAuthStatus } from "@/shared/auth";
import { bindKeyboardHeight, splash, statusBar } from "@/app/platform";
import { verifySessionOrSignOut } from "@/shared/auth";

/**
 * EVERYTHING THAT MUST HAPPEN ONCE, AT START-UP, IN ONE PLACE.
 *
 * Order matters more than it looks: reporting is armed before anything can fail, the
 * session is restored before the router can ask whether anyone is signed in, and the splash
 * is only released after the first paint — so nobody sees the half-second where both
 * answers are still "unknown".
 */
export function AppProviders({ children }: { children: ReactNode }) {
  const [verdict, setVerdict] = useState<VersionVerdict>("ok");
  const [versionInfo, setVersionInfo] = useState<MinimumVersion | undefined>();

  useEffect(() => {
    void initObservability();

    /**
     * A 401 does not sign anyone out on its own. The cache hands the question here, and the
     * auth layer asks the service whether the session is genuinely gone — a phone changing
     * cells, a proxy, or a race with a refresh all produce a single 401, and signing out on
     * one is the difference between an app that blips and an app that "logs me out at
     * random". Concurrent failures collapse into one check.
     */
    setSessionLostHandler(() => void verifySessionOrSignOut());

    void checkVersion().then(({ verdict: v, info }) => {
      setVerdict(v);
      setVersionInfo(info);
    });

    splash.hideWhenReady();
    const unbindKeyboard = bindKeyboardHeight();
    const unbindStatusBar = statusBar.bindToColorScheme();

    return () => {
      unbindKeyboard();
      unbindStatusBar();
    };
  }, []);

  useEffect(
    () =>
      onGlobalApiError((code) => {
        if (code === API_ERRORS.APP_TOO_OLD) setVerdict("update-required");
      }),
    [],
  );

  if (verdict === "update-required") return <UpdateGate info={versionInfo} />;

  return (
    <QueryClientProvider client={queryClient}>
      <IdentifyReporter />
      {children}
      <ErrorToast />
    </QueryClientProvider>
  );
}

/**
 * Ties reports to whoever is signed in — an id only, never an email or a name (the scrubber
 * in app/observability is the second wall). Inside the provider because it reads the session
 * from the cache, which does not exist above it.
 */
function IdentifyReporter() {
  const { user } = useAuthStatus();
  useEffect(() => identifyUser(user?.id ?? null), [user?.id]);
  return null;
}
